import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import JsonCodeEditor from "../../../components/JsonCodeEditor";
import { AppSlideOver } from "../../../components/layout";
import type { PrepJobRunResponse, PrepRunFailedRecord, PrepRunSuccessRecord } from "../../../types/prep";

type Props = {
  open: boolean;
  run: PrepJobRunResponse | null;
  loading?: boolean;
  onClose: () => void;
};

type RecordTab = "success" | "failed" | "summary";

function statusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "PARTIALLY_COMPLETED":
      return "info";
    case "FAILED":
      return "error";
    case "RUNNING":
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
}

function formatPayloadJson(payload?: Record<string, unknown>): string {
  if (!payload || Object.keys(payload).length === 0) {
    return "{}";
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

function recordKey(prefix: string, record: PrepRunSuccessRecord | PrepRunFailedRecord, index: number): string {
  return `${prefix}-${record.documentId ?? "doc"}-${record.sourceId ?? index}-${index}`;
}

function SuccessRecordAccordion({ record, index }: { record: PrepRunSuccessRecord; index: number }) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  return (
    <Accordion disableGutters elevation={0} sx={custom.components.dataPrep.accordionBordered}>
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Stack spacing={0.25} minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {record.documentId ?? t("prep.runs.recordUnknownDocument")}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {t("prep.runs.recordSourceId")}: {record.sourceId ?? "—"}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={custom.components.dataPrep.accordionDetailsNoPaddingTop}>
        <Typography variant="caption" color="text.secondary" sx={custom.components.dataPrep.runDetailFieldLabel}>
          {t("prep.runs.recordPayload")}
        </Typography>
        <Box sx={custom.components.dataPrep.jsonEditorFrame}>
          <JsonCodeEditor
            value={formatPayloadJson(record.payload)}
            onChange={() => undefined}
            readOnly
            height={220}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function FailedRecordAccordion({ record, index }: { record: PrepRunFailedRecord; index: number }) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  return (
    <Accordion disableGutters elevation={0} sx={custom.components.dataPrep.accordionBordered}>
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Stack spacing={0.25} minWidth={0} flex={1}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {record.documentId ?? t("prep.runs.recordUnknownDocument")}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={custom.components.dataPrep.recordErrorCaption}>
            {[record.errorType, record.errorReason].filter(Boolean).join(" · ") || "—"}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={custom.components.dataPrep.accordionDetailsNoPaddingTop}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("prep.runs.recordSourceId")}: {record.sourceId ?? "—"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={custom.components.dataPrep.runDetailFieldLabel}>
              {t("prep.runs.recordPayload")}
            </Typography>
            <Box sx={custom.components.dataPrep.jsonEditorFrame}>
              <JsonCodeEditor
                value={formatPayloadJson(record.payload)}
                onChange={() => undefined}
                readOnly
                height={220}
              />
            </Box>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function PrepRunDetailPanel({ open, run, loading, onClose }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const successes = run?.successRecords ?? [];
  const failures = run?.failedRecords ?? [];
  const failedCount = run?.failedRecordCount ?? failures.length;
  const indexedCount = run?.outputRowCount ?? 0;
  const totalCount = run?.totalRecordCount ?? indexedCount + failedCount;

  const defaultTab = useMemo<RecordTab>(() => {
    if (failures.length > 0) return "failed";
    if (successes.length > 0) return "success";
    if (run?.errors?.length) return "summary";
    return "success";
  }, [failures.length, successes.length, run?.errors?.length]);

  const [tab, setTab] = useState<RecordTab>(defaultTab);

  useEffect(() => {
    if (open) {
      setTab(defaultTab);
    }
  }, [open, run?.id, defaultTab]);

  const activeTab = tab;

  return (
    <AppSlideOver open={open} onClose={onClose} size="xl" title={t("prep.runs.runDetailTitle")}>
      {run && (
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={run.status} color={statusColor(run.status)} />
          <Typography variant="body2" color="text.secondary">
            {t("prep.runs.runDetailStats", {
              indexed: indexedCount,
              failed: failedCount,
              total: totalCount,
            })}
          </Typography>
        </Stack>
      )}

      {run && !loading && (
        <Tabs
          value={activeTab}
          onChange={(_, value: RecordTab) => setTab(value)}
          variant="fullWidth"
          sx={custom.components.dataPrep.runDetailTabs}
        >
          <Tab
            value="success"
            icon={<CheckCircleOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={t("prep.runs.successRecordsTitle", { count: indexedCount })}
          />
          <Tab
            value="failed"
            icon={<ErrorOutlineOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={t("prep.runs.failedRecordsTitleTab", { count: failedCount })}
          />
          {(run.errors?.length ?? 0) > 0 && (
            <Tab
              value="summary"
              icon={<ReportOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t("prep.runs.generalErrors")}
            />
          )}
        </Tabs>
      )}

      <Box sx={custom.components.dataPrep.runDetailBody}>
        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t("prep.runs.loadingDetail")}
            </Typography>
          </Stack>
        )}

        {!loading && run && activeTab === "summary" && (
          <Stack spacing={1}>
            {run.errors?.map((message, index) => (
              <Alert key={index} severity="error" sx={custom.pages.shared.alertCompactPyQuarter}>
                {message}
              </Alert>
            ))}
          </Stack>
        )}

        {!loading && run && activeTab === "success" && (
          <Stack spacing={1}>
            {successes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {indexedCount > 0 ? t("prep.runs.noSuccessRecordDetails") : t("prep.runs.noSuccessRecords")}
              </Typography>
            ) : (
              <>
                {successes.map((record, index) => (
                  <SuccessRecordAccordion key={recordKey("success", record, index)} record={record} index={index} />
                ))}
                {indexedCount > successes.length && (
                  <Typography variant="caption" color="text.secondary">
                    {t("prep.runs.successRecordsTruncated", { shown: successes.length, total: indexedCount })}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}

        {!loading && run && activeTab === "failed" && (
          <Stack spacing={1}>
            {failures.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {failedCount > 0 ? t("prep.runs.noFailedRecordDetails") : t("prep.runs.noFailedRecords")}
              </Typography>
            ) : (
              <>
                {failures.map((record, index) => (
                  <FailedRecordAccordion key={recordKey("failed", record, index)} record={record} index={index} />
                ))}
                {failedCount > failures.length && (
                  <Typography variant="caption" color="text.secondary">
                    {t("prep.runs.failedRecordsTruncated", { shown: failures.length, total: failedCount })}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}
      </Box>
    </AppSlideOver>
  );
}
